import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { RuntimeConfigService } from '../common/runtime-config.service';
// TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
import { recordLivenessDiagnostic } from './liveness-probe-diagnostics';

/**
 * Stream keys identifying which BehaviorSubject slice in
 * FilingFormStateService the remote tab has just mutated.
 */
export type CrossTabSyncStreamKey =
  | 'modifiedState'
  | 'originalState'
  | 'rowStatus'
  | 'sheetStatus'
  | 'edited'
  | 'submissionCommitted'
  // A tab is leaving the form and wants other tabs on the SAME conversion to
  // leave too (redirect to the listing). Sent on cancel/revert (so siblings
  // don't re-read an emptied original and flip every sheet to the pencil icon)
  // AND on resubmit-for-validation (so siblings stop editing now-stale data
  // while the committed conversion re-enters backend validation). This is a
  // notify/intent-only stream — it carries no state slice to rehydrate.
  | 'conversionReset'
  // A tab discarded every unsaved change on this conversion ("Continue without
  // saving" on the leave prompt) and other tabs must return to the baseline
  // WITHOUT leaving the form. Distinct from `conversionReset`, which evicts
  // siblings to the listing.
  //
  // Why this needs its own intent rather than riding the ordinary
  // `modifiedState` notify: `discardModifiedChanges()` is normally followed by
  // the discarding tab tearing its persisted state down, so the receiver's
  // `getModified()` read comes back ABSENT — and the R2 guard then (correctly,
  // to avoid lighting every pencil) refuses to act on it. The sibling was
  // therefore told nothing it was willing to use and kept rendering discarded
  // values. This stream carries the intent instead of relying on a blob that
  // may legitimately be gone; receivers revert modified→original from memory.
  | 'conversionDiscarded'
  // Liveness probe, request half. A tab that is deciding whether it is the LAST
  // holder of a conversion asks "is anyone else still on this conversion?".
  // Intent-only: it carries no state slice, and a tab that does not hold the
  // conversion simply never answers.
  | 'livenessPing'
  // Liveness probe, answer half. Sent ONLY by a tab that already holds the
  // conversion and has settled its own hydration — see
  // `FilingFormStateService.answerLivenessPing`. Its mere arrival is the whole
  // payload.
  | 'livenessPong'
  // A tab is leaving the filing form and is giving up the ONE sheet it had on
  // screen. Intent-only, and the only stream on which `sheetKey` is REQUIRED
  // rather than advisory.
  //
  // This is the per-sheet sibling of `livenessPing`, and the asymmetry between
  // them is forced by where each is sent from. `livenessPing` is sent by a tab
  // ARRIVING, which can await an answer; this one is sent by a tab LEAVING,
  // inside `pagehide`, where there is no async budget at all. So the leaver
  // cannot collect the answers — it states the fact and dies. Each receiver
  // runs its own grace timer and reaches its own verdict, which is why no
  // coordination is needed: every tab is deciding the same question from the
  // same evidence.
  | 'sheetReleased'
  // Answer half of `sheetReleased`: "I am still mounted on that conversion AND
  // that sheet, so its unsaved changes are not abandoned." Sent only by a tab
  // whose FORM is currently showing the released sheet — see
  // `FilingFormStateService.registerMountedSheetProbe`.
  | 'sheetHeldPong';

/**
 * Notify-only payload broadcast across browser tabs when one tab
 * mutates filing-form state. The receiving tab reads fresh data
 * from localStorage rather than trusting channel payload content.
 */
export interface CrossTabSyncMessage {
  type: 'state-change';
  streamKey: CrossTabSyncStreamKey;
  conversionId: string;
  /**
   * Optional sheet key scope (not all streams are per-sheet).
   *
   * Developer note: in the current implementation this will almost always be
   * `undefined`, because `FilingFormStateService.notifyCrossTab(...)` is
   * called from the `saveModifiedState()` / `saveOriginalState()` funnels,
   * which do not know which specific sheet triggered the write. Every write
   * path (updateSheetData, setSheetData, loadFromAPI, revertChanges, etc.)
   * converges on those funnels, so the funnel-level notify is the simplest
   * hook point.
   *
   * This is intentional and NOT a correctness issue:
   *   - The receiver always re-reads FULL localStorage on rehydrate
   *     (`rehydrateModifiedStateFromStorage(sheetKey?)` treats `undefined`
   *     as "rehydrate everything"), so no change is ever missed.
   *   - Downstream, each component's own `distinctUntilChanged` hash over
   *     its watched slices ensures only truly-changed slices trigger
   *     re-validation. Unaffected components see the notify but short-circuit.
   *   - Cross-conversion safety is enforced by `conversionId`, not `sheetKey`.
   *
   * If future log-readability or a perf optimization ever justifies it,
   * threading an explicit `sheetKey` through `saveModifiedState` /
   * `saveOriginalState` (and the ~13 write-sites feeding them) would populate
   * this field. For now the undefined value is accepted by design.
   */
  sheetKey?: string;
  /**
   * Optional list of state slices the sender explicitly declares as
   * layout-affecting. Today only populated by the `submissionCommitted`
   * streamKey so receivers can decide whether the committed payload overlaps
   * with the slices their predicate watches. Receivers MUST still re-read
   * state before comparing — this field is a routing hint, not a payload.
   */
  affectedSlices?: string[];
  /** UUID of the sender tab, used to filter self-echoes on the storage fallback. */
  tabId: string;
  /** Epoch millis at the time of broadcast. Helps debug, not used for ordering. */
  timestamp: number;
}

/**
 * CrossTabSyncService
 *
 * Transport layer for multi-tab synchronization of
 * {@link FilingFormStateService} state. Uses BroadcastChannel when
 * available and falls back to the `storage` window event otherwise.
 *
 * Design: **notify-only**. Payloads carry only enough metadata to tell
 * the receiver which slice changed; localStorage is the source of truth,
 * and the receiver rehydrates from there. This keeps channel traffic
 * small and avoids cross-tab serialization mismatches.
 *
 * Self-echo handling:
 *   - BroadcastChannel: the sender never receives its own messages (by spec).
 *   - `storage` event:  the writer's own window also never receives it (by spec).
 *   - Per-tab UUID is still attached to every message so callers can
 *     defend against edge cases (e.g., when both transports accidentally
 *     coexist in testing scenarios).
 *
 * NgZone: both transports fire outside Angular's zone. Consumers should
 * wrap their state mutations in `zone.run(...)` themselves, or subscribe
 * via the zone-aware stream this service exposes (it re-enters the zone
 * before emitting).
 */
@Injectable({ providedIn: 'root' })
export class CrossTabSyncService {
  private static readonly CHANNEL_NAME = 'mbrs-filing-form-sync';
  /** Storage key used only as a signaling mechanism on the fallback path. */
  private static readonly STORAGE_SIGNAL_KEY = '__mbrs-cross-tab-sync-signal__';

  /**
   * Debounce window for outbound notifications, in milliseconds.
   *
   * Rationale: typical typing cadence is ~150-250ms between keystrokes;
   * 300ms flushes shortly after the user pauses, which is when the
   * receiving tab's validation is actually useful. Not so long that the
   * sync feels laggy when the user commits a field (blur/tab-away).
   *
   * Coalescing semantics: only the LAST notify() within the window per
   * streamKey is broadcast. Receivers always read FULL localStorage on
   * rehydrate, so dropping intermediate sheetKey values is safe — the
   * receiver still picks up all changes.
   */
  private static readonly NOTIFY_DEBOUNCE_MS = 300;

  /**
   * How long {@link probeLiveSiblings} waits for a sibling to answer before
   * reporting none. See that method for why this is sized for a BUSY sibling
   * rather than for a fast one.
   */
  static readonly LIVENESS_PROBE_TIMEOUT_MS = 1000;

  /**
   * How long a tab waits after a {@link CrossTabSyncStreamKey} `sheetReleased`
   * before concluding that no sibling still has that sheet on screen.
   *
   * Deliberately the same budget as {@link LIVENESS_PROBE_TIMEOUT_MS}, for the
   * same reason: the failure direction of a missed answer is DATA LOSS (a
   * sheet's unsaved edits are dropped in every tab), so the window is sized for
   * a sibling whose main thread is busy — a background validation cycle walks
   * 24 sheets synchronously — not for a sibling that is idle. An idle sibling
   * answers in about a millisecond and the wait is never paid.
   *
   * Unlike the liveness probe, this wait costs the user nothing when it IS paid
   * in full: nobody is blocked on it. It runs in a tab that is sitting on some
   * other sheet, and its only effect is how quickly that tab's sheet selector
   * drops the pencil icon.
   */
  static readonly SHEET_RELEASE_GRACE_MS = 1000;

  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly runtimeConfig = inject(RuntimeConfigService);

  /**
   * Combined gate for the `[CrossTabSync]…` debug logs. The build-time
   * `!environment.production` outer check lets the minifier dead-code-
   * eliminate the entire branch from the production bundle; the runtime
   * `crossTabSyncDebug` flag controls visibility in non-prod environments
   * so ops can flip it via `/etc/mbrs/frontend-overrides.json` without a
   * rebuild.
   */
  private isDebugEnabled(): boolean {
    return !environment.production && this.runtimeConfig.crossTabSyncDebug();
  }

  private readonly tabId: string = this.generateTabId();
  private readonly channel: BroadcastChannel | null;
  private readonly messageSubject = new Subject<CrossTabSyncMessage>();

  /**
   * Per-streamKey debounce buffers. Each streamKey has its own Subject so
   * a `modifiedState` burst doesn't delay a `sheetStatus` flush (or vice
   * versa). Lazily created on first notify() for that streamKey.
   */
  private readonly notifyBuffers = new Map<CrossTabSyncStreamKey, Subject<CrossTabSyncMessage>>();

  /** Bound reference so we can detach on destroy. */
  private readonly storageListener = (ev: StorageEvent) => this.handleStorageEvent(ev);

  /** Stream of remote messages (self-echoes already filtered). */
  public readonly messages$: Observable<CrossTabSyncMessage> = this.messageSubject.asObservable();

  constructor() {
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
    // Purpose: proves the service is actually being instantiated.
    // Remove once cross-tab sync is confirmed working end-to-end.
    // console.log('[CrossTabSync][constructor]', {
    //   tabId: this.tabId,
    //   broadcastChannelAvailable: typeof BroadcastChannel !== 'undefined',
    //   origin: typeof location !== 'undefined' ? location.origin : '(no location)',
    // });
    // ----------------------
    // Feature-detect BroadcastChannel. Prefer it over the storage fallback.
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CrossTabSyncService.CHANNEL_NAME);
      this.channel.onmessage = (ev: MessageEvent<CrossTabSyncMessage>) => {
        // ----------------------
        // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
        // Purpose: To prove raw "BroadcastChannel" messages arrive.
        // console.log('[CrossTabSync][onmessage:raw]', {
        //   transport: 'BroadcastChannel',
        //   data: ev.data,
        //   myTabId: this.tabId,
        // });
        // ----------------------
        this.dispatchRemote(ev.data);
      };
    }
    else {
      this.channel = null;
      // Fallback: listen to localStorage writes from other tabs.
      // We only use this when BroadcastChannel is unavailable, to avoid
      // double-emission when both transports see the same event.
      window.addEventListener('storage', this.storageListener);
    }

    this.destroyRef.onDestroy(() => this.teardown());
  }

  /**
   * Enqueue a state-change notification for broadcast to other tabs.
   *
   * Called by FilingFormStateService after every local write (immediately
   * after the BehaviorSubject.next() and the localStorage write have
   * settled). The receiver will rehydrate from localStorage, so the
   * write MUST complete before this call.
   *
   * Debouncing: notifications are coalesced per-streamKey over a
   * {@link NOTIFY_DEBOUNCE_MS}ms window. During a burst of rapid calls
   * (e.g., per-keystroke `valueChanges` emissions), only the LAST call
   * for each streamKey is actually broadcast. This prevents the
   * receiving tab from being spammed with one full rehydrate per
   * keystroke. Different streamKeys debounce independently.
   */
  notify(payload: Omit<CrossTabSyncMessage, 'tabId' | 'timestamp' | 'type'>): void {
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
    // Purpose: To prove notify() is actually being called from a
    // write-site. If you see [constructor] but never [notify-entry], the write path
    // the user exercised is not wired to emit. After the debounce was added,
    // expect MANY [notify-entry] logs but only ONE [send] log per ~300ms
    // pause in the typing burst.
    // console.log('[CrossTabSync][notify-entry]', {
    //   streamKey: payload.streamKey,
    //   sheetKey: payload.sheetKey,
    //   conversionId: payload.conversionId,
    //   myTabId: this.tabId,
    // });
    // ----------------------
    const message: CrossTabSyncMessage = {
      type: 'state-change',
      ...payload,
      tabId: this.tabId,
      timestamp: Date.now(),
    };

    const buffer = this.getOrCreateNotifyBuffer(payload.streamKey);
    buffer.next(message);
  }

  /**
   * Broadcast a message IMMEDIATELY, bypassing the per-streamKey debounce.
   *
   * Use for one-shot, latency-sensitive signals (NOT high-frequency state
   * writes) where the ~{@link NOTIFY_DEBOUNCE_MS}ms coalescing delay would be
   * harmful. The motivating case is `conversionReset`: a tab that cancels/
   * reverts must get the OTHER tabs to navigate away from the conversion before
   * a separate, debounced `originalState` notify can land and momentarily flip
   * their sheet statuses. Sending immediately lets the receivers leave first.
   *
   * Self-echo and transport are identical to {@link notify} — only the
   * debounce is skipped.
   */
  notifyImmediate(payload: Omit<CrossTabSyncMessage, 'tabId' | 'timestamp' | 'type'>): void {
    const message: CrossTabSyncMessage = {
      type: 'state-change',
      ...payload,
      tabId: this.tabId,
      timestamp: Date.now(),
    };
    this.broadcast(message);
  }

  /**
   * Ask whether any OTHER tab currently holds `conversionId`, resolving `true`
   * as soon as one answers and `false` if none has by `timeoutMs`.
   *
   * Liveness is proven by a REPLY, never inferred from a record. That is the
   * whole reason this is a probe rather than a registry of live tabs in
   * `localStorage`: a tab that crashes, is force-killed, or has its process
   * reaped never gets to deregister, so a registry accumulates phantom entries
   * that answer "someone is still there" forever. Nothing here can go stale —
   * a dead tab simply does not reply.
   *
   * The caller decides what silence means. For its one caller today —
   * `FilingFormStateService`'s last-tab-out discard — silence means "discard
   * this conversion's unsaved edits", so the failure direction of a missed
   * reply is DATA LOSS. Two things follow, and both are deliberate:
   *
   *   - {@link LIVENESS_PROBE_TIMEOUT_MS} is generous rather than snappy. An
   *     idle sibling answers in about a millisecond; the budget exists for a
   *     sibling whose main thread is busy (a background validation cycle runs
   *     24 sheets synchronously), because being slow must not be mistaken for
   *     being dead.
   *   - The wait is only ever PAID IN FULL when nobody answers. A live sibling
   *     short-circuits it, so the cost lands on the case where the user is
   *     waiting to be shown clean baseline values anyway.
   *
   * The timer runs OUTSIDE Angular's zone: this is awaited inside the hydration
   * suspension, and a zone-tracked macrotask there would hold the application
   * unstable for the whole window.
   */
  probeLiveSiblings(
    conversionId: string,
    timeoutMs: number = CrossTabSyncService.LIVENESS_PROBE_TIMEOUT_MS
  ): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let settled = false;
      // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
      const startedAt = Date.now();
      const finish = (alive: boolean, answeredBy?: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        subscription.unsubscribe();
        // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
        recordLivenessDiagnostic('probe:result', conversionId, {
          alive,
          waitedMs: Date.now() - startedAt,
          timeoutMs,
          answeredBy: answeredBy ?? null,
          myTransportTabId: this.tabId,
        });
        resolve(alive);
      };

      const subscription = this.messageSubject.subscribe((msg) => {
        if (msg.streamKey === 'livenessPong' && msg.conversionId === conversionId)
          finish(true, msg.tabId);
      });

      const timer = this.zone.runOutsideAngular(() =>
        setTimeout(() => finish(false), timeoutMs)
      );

      // Sent AFTER the listener is attached — a sibling answers synchronously
      // from its own message handler, and on the storage fallback the reply can
      // land in the same task.
      // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
      recordLivenessDiagnostic('probe:sent', conversionId, {
        timeoutMs,
        myTransportTabId: this.tabId,
      });
      this.notifyImmediate({ streamKey: 'livenessPing', conversionId });
    });
  }

  /**
   * Answer someone else's {@link probeLiveSiblings}. Undebounced by nature —
   * the asker is holding a timer open.
   *
   * Deliberately dumb: this service does not know whether this tab holds the
   * conversion or has finished hydrating, so it does not decide whether to
   * answer. `FilingFormStateService` owns that judgement.
   */
  answerLivenessPing(conversionId: string): void {
    this.notifyImmediate({ streamKey: 'livenessPong', conversionId });
  }

  /**
   * Announce that this tab is leaving the filing form and is giving up
   * `sheetKey` on `conversionId`.
   *
   * MUST be undebounced, and must stay synchronous end to end. The motivating
   * caller is the container's `pagehide` handler, where the document is already
   * being torn down: a debounce buffer, a microtask, or a promise would all
   * lose the message. `BroadcastChannel.postMessage` is synchronous, so a call
   * made here does reach sibling tabs even though this one never sees another
   * task — that is the whole reason the release is a broadcast and not a
   * request/response.
   *
   * Developer Note (revisit after launch): see the design rationale on
   * `FilingFormStateService.releaseMountedSheetAcrossTabs`. This handshake is
   * the launch-scoped answer to per-sheet abandonment, not the durable one.
   */
  releaseSheet(conversionId: string, sheetKey: string): void {
    this.notifyImmediate({ streamKey: 'sheetReleased', conversionId, sheetKey });
  }

  /**
   * Answer someone else's {@link releaseSheet} — "that sheet is still on my
   * screen, do not discard it".
   *
   * Deliberately dumb in the same way {@link answerLivenessPing} is: this
   * service does not know which sheet this tab is showing, so it does not
   * decide whether to answer. `FilingFormStateService` owns that judgement, and
   * delegates it to whichever container is currently mounted.
   */
  answerSheetHeld(conversionId: string, sheetKey: string): void {
    this.notifyImmediate({ streamKey: 'sheetHeldPong', conversionId, sheetKey });
  }

  /**
   * Lazily create (and subscribe) a per-streamKey debounce buffer. The
   * subscription is auto-torn-down when this service is destroyed.
   */
  private getOrCreateNotifyBuffer(streamKey: CrossTabSyncStreamKey): Subject<CrossTabSyncMessage> {
    let buffer = this.notifyBuffers.get(streamKey);

    if (buffer)
      return buffer;

    buffer = new Subject<CrossTabSyncMessage>();
    buffer
      .pipe(
        debounceTime(CrossTabSyncService.NOTIFY_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((msg) => this.broadcast(msg));
    this.notifyBuffers.set(streamKey, buffer);
    return buffer;
  }

  /**
   * Actual transport. Called by the debounced subscription above — NOT
   * directly by notify(). Performs the BroadcastChannel postMessage (or
   * storage-event fallback) and emits the `[send]` debug log.
   *
   * Refresh the timestamp to the moment of broadcast so receivers see
   * an accurate "when did this leave the sender" value, not the
   * timestamp of the first buffered notify in the burst.
   */
  private broadcast(message: CrossTabSyncMessage): void {
    const stamped: CrossTabSyncMessage = {
      ...message,
      timestamp: Date.now(),
    };

    if (this.channel) {
      try {
        this.channel.postMessage(stamped);
        // ----------------------
        // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
        // if (this.isDebugEnabled()) {
        //   console.log('[CrossTabSync][send][postMessage]', {
        //     transport: 'BroadcastChannel',
        //     streamKey: stamped.streamKey,
        //     sheetKey: stamped.sheetKey,
        //     conversionId: stamped.conversionId,
        //     tabId: stamped.tabId,
        //     timestamp: stamped.timestamp,
        //   });
        // }
        // ----------------------
      }
      catch (error) {
        // Channel can throw if the message contains non-structured-cloneable
        // data; our payload is plain data so this is defensive only.
        console.error('[CrossTabSync] Failed to postMessage:', error);
      }
      return;
    }

    // Storage-event fallback: write-then-remove a sentinel key whose
    // value is the serialized payload. Other tabs' 'storage' handlers
    // parse `ev.newValue`. The writer's own window does not fire the
    // event, so no self-filter is needed here.
    try {
      localStorage.setItem(CrossTabSyncService.STORAGE_SIGNAL_KEY, JSON.stringify(stamped));
      
      // Immediately clear so rapid successive writes of the same payload
      // still fire a 'storage' event in listeners (newValue must change).
      localStorage.removeItem(CrossTabSyncService.STORAGE_SIGNAL_KEY);
      // ----------------------
      // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
      // if (this.isDebugEnabled()) {
      //   console.log('[CrossTabSync][send][signal]', {
      //     transport: 'storage',
      //     streamKey: stamped.streamKey,
      //     sheetKey: stamped.sheetKey,
      //     conversionId: stamped.conversionId,
      //     tabId: stamped.tabId,
      //     timestamp: stamped.timestamp,
      //   });
      // }
      // ----------------------
    }
    catch (error) {
      console.error('[CrossTabSync] Failed to signal via storage fallback:', error);
    }
  }

  /**
   * Return the current tab's UUID. Exposed for tests and diagnostics.
   */
  getTabId(): string {
    return this.tabId;
  }

  private dispatchRemote(
    message: CrossTabSyncMessage | null | undefined,
    transport: 'BroadcastChannel' | 'storage' = this.channel ? 'BroadcastChannel' : 'storage'
  ): void {
    if (!message || message.type !== 'state-change')
      return;
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
    // if (this.isDebugEnabled()) {
    //   console.log('[CrossTabSync][recv:raw]', {
    //     transport,
    //     streamKey: message.streamKey,
    //     sheetKey: message.sheetKey,
    //     conversionId: message.conversionId,
    //     senderTabId: message.tabId,
    //     myTabId: this.tabId,
    //     timestamp: message.timestamp,
    //   });
    // }
    // ----------------------
    // Self-filter: shouldn't occur on either transport, but guard anyway.
    if (message.tabId === this.tabId) {
      // ----------------------
      // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
      // if (this.isDebugEnabled()) {
      //   console.log('[CrossTabSync][recv:self-echo-dropped]', {
      //     transport,
      //     streamKey: message.streamKey,
      //     sheetKey: message.sheetKey,
      //     tabId: this.tabId,
      //   });
      // }
      // ----------------------
      return;
    }
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
    // if (this.isDebugEnabled()) {
    //   console.log('[CrossTabSync][recv:accepted]', {
    //     transport,
    //     streamKey: message.streamKey,
    //     sheetKey: message.sheetKey,
    //     conversionId: message.conversionId,
    //     senderTabId: message.tabId,
    //     myTabId: this.tabId,
    //   });
    // }
    // ----------------------
    // Re-enter Angular's zone before emitting so downstream subscribers
    // schedule change detection correctly.
    this.zone.run(() => this.messageSubject.next(message));
  }

  private handleStorageEvent(ev: StorageEvent): void {
    if (ev.key !== CrossTabSyncService.STORAGE_SIGNAL_KEY)
      return;

    // removal event from our own writer
    if (!ev.newValue)
      return;
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod) + "crossTabSyncDebug" runtime config
    // Purpose: To prove raw storage-event messages arrive.
    // console.log('[CrossTabSync][onmessage:raw]', {
    //   transport: 'storage',
    //   newValue: ev.newValue,
    //   myTabId: this.tabId,
    // });
    // ----------------------
    try {
      const parsed = JSON.parse(ev.newValue) as CrossTabSyncMessage;
      this.dispatchRemote(parsed, 'storage');
    }
    catch (error) {
      console.error('[CrossTabSync] Failed to parse storage-event payload:', error);
    }
  }

  private teardown(): void {
    if (this.channel) {
      try {
        this.channel.close();
      }
      catch {
        // Ignore — channel may already be closed.
      }
    }
    else {
      window.removeEventListener('storage', this.storageListener);
    }

    // Complete the per-streamKey debounce buffers. The takeUntilDestroyed
    // on each subscription already handles tear-down of the consumer side;
    // completing the producer side is belt-and-braces hygiene.
    this.notifyBuffers.forEach((buffer) => buffer.complete());
    this.notifyBuffers.clear();
    this.messageSubject.complete();
  }

  private generateTabId(): string {
    const cryptoRef = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
    
    if (cryptoRef && typeof cryptoRef.randomUUID === 'function')
      return cryptoRef.randomUUID();
    
    // Fallback: non-cryptographic but sufficient for self-identification.
    return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
